import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AddBillPdfMethodModal } from "@features/AddBillPdfMethodModal/AddBillPdfMethodModal";
import { Button } from "@components/Button";
import { HouseIcon, SearchIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Input } from "@components/inputs/Input/Input";
import { Modal } from "@components/Modal/Modal";
import { useAddBillModal } from "@hooks/useAddBillModal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { useModalActions, type IUseModalProps } from "@store/modal/modal";
import { useUploadBillFileConfirmMutation } from "@services/billFiles/mutations";
import { useOverviewQuery } from "@services/overview/queries";
import { parseDecimalNumberInputValue } from "@utils/units";
import { AddBillPdfMethodAutomaticParsingModalPdfViewer } from "./AddBillPdfMethodAutomaticParsingModalPdfViewer";
import {
  useAddBillPdfMethodAutomaticParsingModalSchema,
  type TAddBillPdfMethodAutomaticParsingModalSchema,
} from "./useAddBillPdfMethodAutomaticParsingModalSchema";

interface IAddBillPdfMethodAutomaticParsingModalProps extends IUseModalProps {
  file: File;
  billFileId: number;
  serviceProviderId: number;
  serviceProviderName: number;
  isIbanRequired: boolean;
  defaultValues: {
    jointBillServiceId: number;
    customerCode: string;
    total: number;
    iban?: string;
  };
}

export const AddBillPdfMethodAutomaticParsingModal: React.FC<
  IAddBillPdfMethodAutomaticParsingModalProps
> = (props) => {
  const { defaultValues, file, serviceProviderName, isIbanRequired, ...rest } =
    props;
  const modal = useModal(rest);
  const { t } = useTranslation();
  const { create } = useModalActions();
  const { openAddBillModal } = useAddBillModal();

  const { data: overviewData } = useOverviewQuery();

  const openAddBillPdfMethodModal = () => {
    create((modalProps) => (
      <AddBillPdfMethodModal
        {...modalProps}
        onCancel={() => openAddBillModal()}
        preselectedJointBillServiceId={defaultValues.jointBillServiceId}
      />
    ));
    modal.close();
  };

  const schema = useAddBillPdfMethodAutomaticParsingModalSchema(isIbanRequired);

  const { handleSubmit, control, setValue, getValues, setError, watch } =
    useForm<TAddBillPdfMethodAutomaticParsingModalSchema>({
      defaultValues: {
        house: defaultValues.jointBillServiceId,
        customerCode: defaultValues.customerCode,
        iban: defaultValues.iban,
        total: defaultValues.total,
      },
      resolver: zodResolver(schema),
    });

  const houseId = watch("house");
  const houseName = overviewData?.jointBillServices.find(
    (jbs) => jbs.jointBillServiceId === houseId,
  )?.displayName;

  const { setApiErrors } = useApiErrorUtils(setError);

  const { mutateAsync, isPending } = useUploadBillFileConfirmMutation();

  const onSubmit: SubmitHandler<
    TAddBillPdfMethodAutomaticParsingModalSchema
  > = async (data) => {
    const response = await mutateAsync({
      billFileId: props.billFileId,
      jointBillServiceId: data.house,
      iban: data.iban,
      customerCode: data.customerCode ?? "",
      total: Number(data.total),
    }).catch(setApiErrors);

    if (response?.success) {
      toast.success(t("addBillPdfMethodAutomaticParsingModal.toast.success"));
      modal.close();
    } else if (response && !response.success) {
      toast.error(t(`validationErrors.${response.error}`));
    }
  };

  return (
    <Modal
      modal={modal}
      className="md:max-w-6xl"
      title={t("addBillPdfMethodAutomaticParsingModal.title")}
    >
      <div className="flex flex-col gap-4 md:flex-row">
        <AddBillPdfMethodAutomaticParsingModalPdfViewer
          className="h-[80vh] w-full"
          file={file}
        />
        <div className="flex w-full flex-col gap-3 md:max-w-sm">
          <Input
            iconLeft={HouseIcon}
            disabled
            value={houseName ?? t("common.defaultAddress")}
          />
          <Input
            label={t(
              "addBillPdfMethodAutomaticParsingModal.fields.serviceProvider.label",
            )}
            disabled
            iconLeft={SearchIcon}
            value={serviceProviderName}
          />
          <FormInput
            name="customerCode"
            control={control}
            label={t(
              "addBillPdfMethodAutomaticParsingModal.fields.customerCode.label",
            )}
          />
          {isIbanRequired && (
            <FormInput
              name="iban"
              control={control}
              label={t(
                "addBillPdfMethodAutomaticParsingModal.fields.iban.label",
              )}
            />
          )}
          <FormInput
            name="total"
            control={control}
            onBlur={() => {
              setValue(
                "total",
                parseDecimalNumberInputValue(getValues("total").toString()),
              );
            }}
            label={t(
              "addBillPdfMethodAutomaticParsingModal.fields.total.label",
            )}
          />
          <div className="flex flex-grow flex-col justify-end gap-2">
            <Button
              className="w-full"
              isLoading={isPending}
              onClick={handleSubmit(onSubmit)}
            >
              {t("common.form.buttons.continue")}
            </Button>
            <Button
              variant="secondary"
              onClick={openAddBillPdfMethodModal}
              className="w-full"
            >
              {t("common.form.buttons.cancel")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
